import * as React from 'react'
import { FC, useCallback, useEffect, useMemo } from 'react'
import { Formik, FormikHelpers } from 'formik'
import * as yup from 'yup'
import { SalesTeam } from '@user/store/salesTeam/typings'
import { AddSalesTeamForm } from './AddSalesTeamForm'
import { selectAllSalesTeams } from '@user/store/salesTeam/slice'
import { useSelector } from 'react-redux'
import { updateLoadingState } from '@sales-center/common'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { createSalesTeam, updateSalesTeam } from '@user/store/salesTeam/api'
import { toast } from '@sales-center/components'
import { isAPIValidationError } from '@user/store/services/crud'
import { trimString } from '@sales-center/common'

interface Props {
  onClose: () => void
  editTeam?: SalesTeam
}

const validationSchema = yup.object({
  name: yup
    .string()
    .required('Please enter a name.')
    .trim()
    .min(5, 'Name must be between 5 and 100 characters')
    .trim()
    .max(100, 'Name must be between 5 and 100 characters'),
  branchId: yup.string().required('Please enter a branch id.'),
})

export const AddSalesTeamFormWrapper: FC<Props> = (props) => {
  const { editTeam, onClose } = props
  const salesTeams = useSelector(selectAllSalesTeams)
  const dispatch = useAppDispatch()
  const initialValues: SalesTeam = useMemo(
    () => ({
      id: editTeam?.id || 0,
      name: editTeam?.name || '',
      branchDefault: editTeam?.branchDefault || false,
      branchId: editTeam?.branchId || '',
      branchName: editTeam?.branchName || '',
      inUse: editTeam?.inUse || undefined,
      hasAccess: editTeam?.hasAccess || undefined,
      tenantDefault: editTeam?.tenantDefault || false,
      structuredSharing: editTeam?.structuredSharing.toString() || 'true',
    }),
    [editTeam],
  )

  useEffect(() => {
    dispatch(updateLoadingState(status == 'pending' ? 'visible' : 'hidden'))

    if (status == 'success') onClose()
  }, [dispatch, onClose])

  const onSubmit = useCallback(
    async (model: SalesTeam, helpers: FormikHelpers<SalesTeam>) => {
      if (!!editTeam) {
        const result = await dispatch(
          updateSalesTeam({
            id: editTeam.id,
            name: trimString(model.name),
            structuredSharing: model.structuredSharing,
          }),
        )
        if (updateSalesTeam.fulfilled.match(result)) {
          toast.success('Sales Team updated')
          onClose()
        } else {
          const { payload } = result
          if (!!payload) {
            if (isAPIValidationError(payload)) {
              const { errors } = payload
              if (errors) {
                if (errors['Name']) {
                  helpers.setFieldError('name', errors['Name'].join(', '))
                }
              }
            } else {
              toast.error('Unable to update sales team')
            }
          }
        }
      } else {
        const result = await dispatch(
          createSalesTeam({
            ...model,
            name: trimString(model.name),
            branchDefault: !salesTeams.find(
              (team) =>
                team.branchId == model.branchId && team.branchDefault == true,
            ),
          }),
        )
        if (createSalesTeam.fulfilled.match(result)) {
          toast.success('Sales Team created')
          onClose()
        } else {
          if (result.payload) {
            const { payload } = result
            if (!!payload) {
              if (isAPIValidationError(payload)) {
                const { errors } = payload
                if (errors) {
                  if (errors['Name']) {
                    helpers.setFieldError('name', errors['Name'].join(', '))
                  }
                }
              } else {
                toast.error('Unable to create sales team')
              }
            }
          }
        }
      }
    },
    [salesTeams, onClose, editTeam, dispatch],
  )

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      validateOnMount={!!editTeam}
    >
      <AddSalesTeamForm onClose={onClose} editTeam={editTeam} />
    </Formik>
  )
}

AddSalesTeamFormWrapper.displayName = 'AddSalesTeamFormWrapper'
